// Saison et horaires de visite, réglés dans le back-office (Paramètres › Visites guidées) et
// appliqués partout : textes du site (4 langues), données structurées, billets, e-mails.
//
// Les réglages restent stockés comme avant dans app_settings :
//   visit_season_start / visit_season_end : « MM-JJ » (ex. « 04-04 » = 4 avril)
//   visit_schedule_lundi … visit_schedule_dimanche : « 14h00 - 18h00 » ou « Fermé »
//   visit_last_entry : « 16:30 »
// Les textes utilisent des marqueurs ({periode}, {jours_heures}…) remplacés par phrasesOuverture().

export const JOURS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"] as const;
export type Jour = (typeof JOURS)[number];
export type Langue = "fr" | "en" | "de" | "nl";
/** Créneau d'ouverture d'un jour, en minutes depuis minuit. */
export type Creneau = { debut: number; fin: number };

export interface ReglagesOuverture {
  /** « MM-JJ » */
  debut: string;
  /** « MM-JJ », inclus */
  fin: string;
  horaires: Record<Jour, Creneau | null>;
  derniereEntree: number | null;
}

const APRES_MIDI: Creneau = { debut: 14 * 60, fin: 18 * 60 };
export const OUVERTURE_DEFAUT: ReglagesOuverture = {
  debut: "04-04",
  fin: "11-01",
  horaires: { lundi: null, mardi: null, mercredi: APRES_MIDI, jeudi: APRES_MIDI, vendredi: APRES_MIDI, samedi: APRES_MIDI, dimanche: APRES_MIDI },
  derniereEntree: 16 * 60 + 30,
};

// ── Lecture / écriture des réglages ────────────────────────────────────────────────

/** « 14h », « 14h00 », « 14:30 », « 14 h 30 », « 14.30 » → minutes ; null si illisible. */
export function lireHeure(texte: string | null | undefined): number | null {
  const m = String(texte ?? "").trim().match(/^(\d{1,2})\s*(?:[h:.]\s*(\d{2})?)?\s*$/i);
  if (!m) return null;
  const h = Number(m[1]);
  const min = m[2] ? Number(m[2]) : 0;
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** « 14h00 - 18h00 », « 14h-18h », « 14:00–18:00 », « 14h à 18h » → créneau ; « Fermé », vide ou illisible → null. */
export function lireCreneau(texte: string | null | undefined): Creneau | null {
  const parties = String(texte ?? "").trim().split(/\s*(?:-|–|—)\s*|\s+(?:à|a)\s+/i);
  if (parties.length !== 2) return null;
  const debut = lireHeure(parties[0]);
  const fin = lireHeure(parties[1]);
  if (debut === null || fin === null || fin <= debut) return null;
  return { debut, fin };
}

const deuxChiffres = (n: number) => String(n).padStart(2, "0");
export const heureVersTexte = (minutes: number) => `${deuxChiffres(Math.floor(minutes / 60))}:${deuxChiffres(minutes % 60)}`;

/** Format enregistré en base (inchangé) : « 14h00 - 18h00 » ou « Fermé ». */
export function ecrireCreneau(c: Creneau | null): string {
  if (!c) return "Fermé";
  const h = (m: number) => `${deuxChiffres(Math.floor(m / 60))}h${deuxChiffres(m % 60)}`;
  return `${h(c.debut)} - ${h(c.fin)}`;
}

/** « MM-JJ » valide (accepte « 4-4 ») ou null. */
export function lireMoisJour(texte: string | null | undefined): string | null {
  const m = String(texte ?? "").trim().match(/^(\d{1,2})-(\d{1,2})$/);
  if (!m) return null;
  const mois = Number(m[1]);
  const jour = Number(m[2]);
  if (mois < 1 || mois > 12 || jour < 1 || jour > JOURS_PAR_MOIS[mois - 1]) return null;
  return `${deuxChiffres(mois)}-${deuxChiffres(jour)}`;
}
const JOURS_PAR_MOIS = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** Réglages depuis app_settings ; une valeur absente ou illisible garde la valeur par défaut. */
export function reglagesDepuis(settings: Record<string, string | null | undefined>): ReglagesOuverture {
  const horaires = { ...OUVERTURE_DEFAUT.horaires };
  for (const jour of JOURS) {
    const brut = settings[`visit_schedule_${jour}`];
    if (brut == null || brut.trim() === "") continue;
    horaires[jour] = lireCreneau(brut);
  }
  let debut = lireMoisJour(settings.visit_season_start) ?? OUVERTURE_DEFAUT.debut;
  let fin = lireMoisJour(settings.visit_season_end) ?? OUVERTURE_DEFAUT.fin;
  if (fin < debut) [debut, fin] = [OUVERTURE_DEFAUT.debut, OUVERTURE_DEFAUT.fin];
  const entree = settings.visit_last_entry;
  return {
    debut,
    fin,
    horaires,
    derniereEntree: entree == null || entree.trim() === "" ? OUVERTURE_DEFAUT.derniereEntree : lireHeure(entree),
  };
}

/** Réglages → valeurs app_settings. */
export function reglagesVersSettings(r: ReglagesOuverture): Record<string, string> {
  const s: Record<string, string> = {
    visit_season_start: r.debut,
    visit_season_end: r.fin,
    visit_last_entry: r.derniereEntree === null ? "" : heureVersTexte(r.derniereEntree),
  };
  for (const jour of JOURS) s[`visit_schedule_${jour}`] = ecrireCreneau(r.horaires[jour]);
  return s;
}

/** Date du jour à Paris, « AAAA-MM-JJ ». */
export function aujourdhuiParis(maintenant: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit" }).format(maintenant);
}

// ── Vocabulaire ───────────────────────────────────────────────────────────────────

type Vocabulaire = {
  jours: string[]; // lundi → dimanche
  joursCourts: string[];
  mois: string[];
  moisCourts: string[];
  ferme: string;
  et: string; // liste « lundi et mardi »
  etCourt: string; // « lun & mar »
};

const VOCAB: Record<Langue, Vocabulaire> = {
  fr: {
    jours: ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"],
    joursCourts: ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"],
    mois: ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"],
    moisCourts: ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."],
    ferme: "Fermé",
    et: " et ",
    etCourt: " & ",
  },
  en: {
    jours: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    joursCourts: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    mois: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
    moisCourts: ["Jan.", "Feb.", "Mar.", "Apr.", "May", "June", "July", "Aug.", "Sept.", "Oct.", "Nov.", "Dec."],
    ferme: "Closed",
    et: " & ",
    etCourt: " & ",
  },
  de: {
    jours: ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"],
    joursCourts: ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"],
    mois: ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"],
    moisCourts: ["Jan.", "Feb.", "März", "Apr.", "Mai", "Juni", "Juli", "Aug.", "Sept.", "Okt.", "Nov.", "Dez."],
    ferme: "Geschlossen",
    et: " & ",
    etCourt: " & ",
  },
  nl: {
    jours: ["maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag", "zondag"],
    joursCourts: ["ma", "di", "wo", "do", "vr", "za", "zo"],
    mois: ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"],
    moisCourts: ["jan.", "feb.", "mrt.", "apr.", "mei", "juni", "juli", "aug.", "sep.", "okt.", "nov.", "dec."],
    ferme: "Gesloten",
    et: " & ",
    etCourt: " & ",
  },
};

const majuscule = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function liste(elements: string[], et: string) {
  if (elements.length <= 1) return elements.join("");
  return `${elements.slice(0, -1).join(", ")}${et}${elements[elements.length - 1]}`;
}

// Heures : 14h / 14h30 (fr), 2 PM / 2:30 PM (en), 14 / 14:30 (de), 14u / 14u30 (nl, long) et 14 / 14.30 (nl, court).
function heure(langue: Langue, m: number, forme: "long" | "court" = "long") {
  const h = Math.floor(m / 60);
  const mi = m % 60;
  switch (langue) {
    case "fr":
      return mi ? `${h}h${deuxChiffres(mi)}` : `${h}h`;
    case "en": {
      const h12 = h % 12 === 0 ? 12 : h % 12;
      return `${mi ? `${h12}:${deuxChiffres(mi)}` : h12} ${h < 12 ? "AM" : "PM"}`;
    }
    case "de":
      return mi ? `${h}:${deuxChiffres(mi)}` : `${h}`;
    case "nl":
      return forme === "long" ? (mi ? `${h}u${deuxChiffres(mi)}` : `${h}u`) : mi ? `${h}.${deuxChiffres(mi)}` : `${h}`;
  }
}

/** « de 14h à 18h » / « from 2 PM to 6 PM » / « von 14 bis 18 Uhr » / « van 14u tot 18u » */
function plageLongue(langue: Langue, c: Creneau) {
  switch (langue) {
    case "fr":
      return `de ${heure("fr", c.debut)} à ${heure("fr", c.fin)}`;
    case "en":
      return `from ${heure("en", c.debut)} to ${heure("en", c.fin)}`;
    case "de":
      return `von ${heure("de", c.debut)} bis ${heure("de", c.fin)} Uhr`;
    case "nl":
      return `van ${heure("nl", c.debut)} tot ${heure("nl", c.fin)}`;
  }
}

/** « 14h–18h » / « 2–6 PM » / « 14–18 Uhr » / « 14–18 uur » */
function plageCourte(langue: Langue, c: Creneau) {
  switch (langue) {
    case "fr":
      return `${heure("fr", c.debut)}–${heure("fr", c.fin)}`;
    case "en": {
      const memeMoitie = c.debut < 12 * 60 === c.fin < 12 * 60;
      const d = heure("en", c.debut);
      return `${memeMoitie ? d.replace(/ (AM|PM)$/, "") : d}–${heure("en", c.fin)}`;
    }
    case "de":
      return `${heure("de", c.debut)}–${heure("de", c.fin)} Uhr`;
    case "nl":
      return `${heure("nl", c.debut, "court")}–${heure("nl", c.fin, "court")} uur`;
  }
}

/** Valeur d'un jour dans un tableau d'horaires : « 14h à 18h » / « 2–6 PM »… */
function plageJour(langue: Langue, c: Creneau) {
  return langue === "fr" ? `${heure("fr", c.debut)} à ${heure("fr", c.fin)}` : plageCourte(langue, c);
}

/** « dimanche 1er novembre », « Sunday, November 1 », « Sonntag, 1. November », « zondag 1 november » (+ année si demandée). */
function dateLongue(langue: Langue, annee: number, moisJour: string, avecAnnee: boolean) {
  const [mois, jour] = moisJour.split("-").map(Number);
  const v = VOCAB[langue];
  const js = new Date(Date.UTC(annee, mois - 1, jour)).getUTCDay(); // 0 = dimanche
  const nomJour = v.jours[(js + 6) % 7];
  const nomMois = v.mois[mois - 1];
  switch (langue) {
    case "fr":
      return `${nomJour} ${jour === 1 ? "1er" : jour} ${nomMois}${avecAnnee ? ` ${annee}` : ""}`;
    case "en":
      return `${nomJour}, ${nomMois} ${jour}${avecAnnee ? `, ${annee}` : ""}`;
    case "de":
      return `${nomJour}, ${jour}. ${nomMois}${avecAnnee ? ` ${annee}` : ""}`;
    case "nl":
      return `${nomJour} ${jour} ${nomMois}${avecAnnee ? ` ${annee}` : ""}`;
  }
}

function dateCourte(langue: Langue, moisJour: string) {
  const [mois, jour] = moisJour.split("-").map(Number);
  const nomMois = VOCAB[langue].moisCourts[mois - 1];
  if (langue === "fr") return `${jour === 1 ? "1er" : jour} ${nomMois}`;
  if (langue === "en") return `${nomMois} ${jour}`;
  if (langue === "de") return `${jour}. ${nomMois}`;
  return `${jour} ${nomMois}`;
}

type Groupe = { jours: number[]; creneau: Creneau };

/** Jours ouverts regroupés : jours consécutifs (lundi → dimanche) au même créneau. */
function groupes(r: ReglagesOuverture): Groupe[] {
  const res: Groupe[] = [];
  JOURS.forEach((jour, i) => {
    const c = r.horaires[jour];
    if (!c) return;
    const dernier = res[res.length - 1];
    if (dernier && dernier.jours[dernier.jours.length - 1] === i - 1 && dernier.creneau.debut === c.debut && dernier.creneau.fin === c.fin) {
      dernier.jours.push(i);
    } else {
      res.push({ jours: [i], creneau: c });
    }
  });
  return res;
}

function joursDuGroupe(langue: Langue, g: Groupe) {
  const v = VOCAB[langue];
  const premier = v.jours[g.jours[0]];
  const dernier = v.jours[g.jours[g.jours.length - 1]];
  if (g.jours.length === 1) return langue === "fr" ? `le ${premier}` : premier;
  if (g.jours.length === 2) {
    return { fr: `le ${premier} et le ${dernier}`, en: `${premier} and ${dernier}`, de: `${premier} und ${dernier}`, nl: `${premier} en ${dernier}` }[langue];
  }
  return { fr: `du ${premier} au ${dernier}`, en: `${premier} to ${dernier}`, de: `${premier} bis ${dernier}`, nl: `${premier} tot ${dernier}` }[langue];
}

function joursCourtsDuGroupe(langue: Langue, g: Groupe) {
  const v = VOCAB[langue];
  const premier = v.joursCourts[g.jours[0]];
  const dernier = v.joursCourts[g.jours[g.jours.length - 1]];
  if (g.jours.length === 1) return premier;
  if (g.jours.length === 2) return `${premier} & ${dernier}`;
  return `${premier}–${dernier}`;
}

const TOUS_LES_JOURS: Record<Langue, { long: string; court: string }> = {
  fr: { long: "tous les jours", court: "Tous les jours" },
  en: { long: "every day", court: "Daily" },
  de: { long: "täglich", court: "Täglich" },
  nl: { long: "dagelijks", court: "Dagelijks" },
};

const SUR_RENDEZ_VOUS: Record<Langue, string> = {
  fr: "sur rendez-vous",
  en: "by appointment",
  de: "nach Vereinbarung",
  nl: "op afspraak",
};

/**
 * Phrases à insérer dans les textes (marqueurs {cle}). Toutes les clés existent en minuscule et,
 * pour un début de phrase, avec une majuscule ({Periode}…).
 * aujourdHui : « AAAA-MM-JJ » (heure de Paris) ; décide « jusqu'au … » (pendant la saison)
 * ou « du … au … » (saison suivante).
 */
export function phrasesOuverture(r: ReglagesOuverture, langue: Langue, aujourdHui: string): Record<string, string> {
  const v = VOCAB[langue];
  const annee = Number(aujourdHui.slice(0, 4));
  const moisJour = aujourdHui.slice(5, 10);
  const enSaison = moisJour >= r.debut && moisJour <= r.fin;
  const anneeSaison = moisJour > r.fin ? annee + 1 : annee;
  const autreAnnee = anneeSaison !== annee;
  const debutLong = dateLongue(langue, anneeSaison, r.debut, false);
  const finLong = dateLongue(langue, anneeSaison, r.fin, autreAnnee);

  const complete = {
    fr: `du ${debutLong} au ${finLong} inclus`,
    en: `from ${debutLong} to ${finLong} inclusive`,
    de: `von ${debutLong} bis einschließlich ${finLong}`,
    nl: `van ${debutLong} tot en met ${finLong}`,
  }[langue];
  const jusquA = {
    fr: `jusqu'au ${finLong} inclus`,
    en: `until ${finLong} inclusive`,
    de: `bis einschließlich ${finLong}`,
    nl: `tot en met ${finLong}`,
  }[langue];
  const periode = enSaison ? jusquA : complete;

  const gs = groupes(r);
  const ouverts = JOURS.filter((j) => r.horaires[j]);
  const fermes = JOURS.map((_, i) => i).filter((i) => !r.horaires[JOURS[i]]);
  const tousMemeCreneau = gs.length === 1 && gs[0].jours.length === 7;

  let joursHeures: string;
  let joursHeuresCourt: string;
  if (gs.length === 0) {
    joursHeures = SUR_RENDEZ_VOUS[langue];
    joursHeuresCourt = majuscule(SUR_RENDEZ_VOUS[langue]);
  } else if (tousMemeCreneau) {
    joursHeures = `${TOUS_LES_JOURS[langue].long} ${plageLongue(langue, gs[0].creneau)}`;
    joursHeuresCourt = `${TOUS_LES_JOURS[langue].court} · ${plageCourte(langue, gs[0].creneau)}`;
  } else {
    joursHeures = liste(gs.map((g) => `${joursDuGroupe(langue, g)} ${plageLongue(langue, g.creneau)}`), langue === "fr" ? " et " : langue === "en" ? " and " : langue === "de" ? " und " : " en ");
    joursHeuresCourt = gs.map((g) => `${joursCourtsDuGroupe(langue, g)} · ${plageCourte(langue, g.creneau)}`).join(" / ");
  }

  const nomsFermes = fermes.map((i) => v.jours[i]);
  const nomsFermesCourts = fermes.map((i) => v.joursCourts[i].toLowerCase());
  const fermesListe = liste(nomsFermes, v.et);
  const fermesListePhrase = liste(nomsFermes, { fr: " et ", en: " and ", de: " und ", nl: " en " }[langue]);
  const fermesCourt = langue === "en" || langue === "de" ? liste(fermes.map((i) => v.joursCourts[i]), v.etCourt) : liste(nomsFermesCourts, v.etCourt);
  const aDesFermes = fermes.length > 0 && fermes.length < 7;

  const fermesParen = !aDesFermes
    ? ""
    : { fr: ` (fermé ${fermesListe})`, en: ` (closed ${fermesListe})`, de: ` (${fermesListe} geschlossen)`, nl: ` (${fermesListe} gesloten)` }[langue];
  const fermesCourtParen = !aDesFermes
    ? ""
    : { fr: ` (fermé ${fermesCourt})`, en: ` (closed ${fermesCourt})`, de: ` (${fermesCourt} geschlossen)`, nl: ` (${fermesCourt} gesloten)` }[langue];
  const fermesPhrase = !aDesFermes
    ? ""
    : { fr: ` ${majuscule(fermesListePhrase)} : fermé.`, en: ` Closed on ${fermesListePhrase}.`, de: ` ${majuscule(fermesListePhrase)} geschlossen.`, nl: ` ${majuscule(fermesListePhrase)} gesloten.` }[langue];

  // Amplitude (premier accueil → dernière fermeture), pour « accueilli entre 14 h et 18 h ».
  const amplitude: Creneau | null = ouverts.length
    ? { debut: Math.min(...ouverts.map((j) => r.horaires[j]!.debut)), fin: Math.max(...ouverts.map((j) => r.horaires[j]!.fin)) }
    : null;
  const entreHeures = !amplitude
    ? SUR_RENDEZ_VOUS[langue]
    : {
        fr: `entre ${heure("fr", amplitude.debut)} et ${heure("fr", amplitude.fin)}`,
        en: `between ${heure("en", amplitude.debut).toLowerCase()} and ${heure("en", amplitude.fin).toLowerCase()}`,
        de: `zwischen ${heure("de", amplitude.debut)} und ${heure("de", amplitude.fin)} Uhr`,
        nl: `tussen ${heure("nl", amplitude.debut, "court")} en ${heure("nl", amplitude.fin, "court")} uur`,
      }[langue];

  const lignesJours = JOURS.map((jour, i) => {
    const c = r.horaires[jour];
    return c ? `${majuscule(v.jours[i])}${langue === "fr" ? " : " : ": "}${plageJour(langue, c)}` : null;
  }).filter((l): l is string => l !== null);
  const listeHoraires = [
    ...(aDesFermes ? [`${v.ferme}${langue === "fr" ? " : " : ": "}${langue === "fr" ? majuscule(fermesListe) : fermesListe}`] : []),
    ...lignesJours,
  ].join("\n");

  const p: Record<string, string> = {
    periode,
    periode_complete: complete,
    fin_saison: finLong,
    debut_saison: debutLong,
    periode_courte: `${dateCourte(langue, r.debut)} – ${dateCourte(langue, r.fin)}`,
    mois_debut: v.mois[Number(r.debut.slice(0, 2)) - 1],
    mois_fin: v.mois[Number(r.fin.slice(0, 2)) - 1],
    jours_heures: joursHeures,
    jours_heures_court: joursHeuresCourt,
    entre_heures: entreHeures,
    fermes: fermesListe,
    fermes_paren: fermesParen,
    fermes_court_paren: fermesCourtParen,
    fermes_phrase: fermesPhrase,
    liste_horaires: listeHoraires,
    ferme: v.ferme,
    derniere_entree: r.derniereEntree === null ? "" : langue === "en" ? heure("en", r.derniereEntree) : langue === "de" ? `${heure("de", r.derniereEntree)} Uhr` : heure(langue, r.derniereEntree),
  };
  JOURS.forEach((jour) => {
    const c = r.horaires[jour];
    p[`h_${jour}`] = c ? plageJour(langue, c) : v.ferme;
  });
  // Variantes avec majuscule initiale pour les débuts de phrase.
  for (const [cle, valeur] of Object.entries({ ...p })) p[majuscule(cle)] = majuscule(valeur);
  return p;
}

/** Remplace les marqueurs {cle} connus ; les autres accolades restent telles quelles. */
export function insererPhrases(texte: string, phrases: Record<string, string>): string {
  if (!texte.includes("{")) return texte;
  return texte.replace(/\{([A-Za-z_]+)\}/g, (tout, cle: string) => (cle in phrases ? phrases[cle] : tout));
}

/** Données injectées dans les pages (window.__OUVERTURE__) : réglages + jour de référence. */
export type OuverturePage = { reglages: ReglagesOuverture; jour: string };

/** openingHoursSpecification schema.org (saison en cours ou suivante). */
export function horairesSchemaOrg(r: ReglagesOuverture, aujourdHui: string) {
  const annee = Number(aujourdHui.slice(0, 4));
  const anneeSaison = aujourdHui.slice(5, 10) > r.fin ? annee + 1 : annee;
  const NOMS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  return groupes(r).map((g) => ({
    "@type": "OpeningHoursSpecification",
    dayOfWeek: g.jours.map((i) => NOMS[i]),
    opens: heureVersTexte(g.creneau.debut),
    closes: heureVersTexte(g.creneau.fin),
    validFrom: `${anneeSaison}-${r.debut}`,
    validThrough: `${anneeSaison}-${r.fin}`,
  }));
}
